import { useState } from "react";
import { changePassword } from "../services/changePasswordService";
import toast from "react-hot-toast";
import { UserPen, EyeOff, Eye } from "lucide-react";
import "../styles/password.css";
import "../styles/dashboard.css";

export default function ChangePassword() {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [loading, setLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [strength, setStrength] = useState(0);
  const [strengthErrors, setStrengthErrors] = useState([]);

  function getPasswordStrength(password) {
    let score = 0;

    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    return score; // 0–5
  }

  function getStrengthErrors(password) {
    const errors = [];

    if (password.length < 8) errors.push("حداقل 8 کاراکتر لازم است");

    if (!/[A-Z]/.test(password))
      errors.push("حداقل یک حرف بزرگ انگلیسی لازم است");

    if (!/[a-z]/.test(password))
      errors.push("حداقل یک حرف کوچک انگلیسی لازم است");

    if (!/[0-9]/.test(password)) errors.push("حداقل یک عدد لازم است");

    if (!/[^A-Za-z0-9]/.test(password))
      errors.push("حداقل یک کاراکتر خاص لازم است");

    return errors;
  }

  const handleSubmit = () => {
    const errors = {};

    if (!form.current_password.trim())
      errors.current_password = "کلمه عبور عبور فعلی را وارد کنید";

    if (!form.new_password.trim())
      errors.new_password = "کلمه عبور عبور جدید را وارد کنید";

    if (form.new_password.length < 6)
      errors.new_password = "کلمه عبور عبور باید حداقل 6 کاراکتر باشد";

    if (form.confirm_password !== form.new_password)
      errors.confirm_password = "کلمه عبور عبور جدید و تکرار آن یکسان نیست";

    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach((key) => {
        toast.error(errors[key], { duration: 5000 });
      });
      return;
    }

    setLoading(true);

    changePassword(form)
      .then((res) => {
        toast.success(res.message, { duration: 4000 });
        setLoading(false);

        setForm({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
      })
      .catch((response) => {
        setLoading(false);

        if (response?.status === 422) {
          const data = response.data;

          Object.keys(data.errors).forEach((field) => {
            toast.error(<span>{data.errors[field][0]}</span>, {
              duration: 5000,
            });
          });
        } else {
          toast.error("خطا در تغییر کلمه عبور عبور");
        }
      });
  };

  return (
    <div className="wrapper">
      <h2>
        <span>تغییر کلمه عبور</span>
      </h2>
      <div className="change-password-page">
        <div className="form-group">
          <input
            id="password"
            type={showCurrent ? "text" : "password"}
            placeholder=" "
            value={form.current_password}
            onChange={(e) =>
              setForm({ ...form, current_password: e.target.value })
            }
            className="password-input"
            autoComplete="new-password"
          />

          <label htmlFor="password">کلمه عبور فعلی</label>

          <div
            className="eye-icon"
            onClick={() => setShowCurrent(!showCurrent)}
          >
            {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <input
              id="new_password"
              type={showNew ? "text" : "password"}
              value={form.new_password}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ ...form, new_password: value });

                setStrength(getPasswordStrength(value));
                setStrengthErrors(getStrengthErrors(value));
              }}
              placeholder=" "
              className="password-input"
              autoComplete="new-password"
            />

            <label className="new-assword-label" htmlFor="new_password">
              کلمه عبور جدید
            </label>
            <div className="eye-icon" onClick={() => setShowNew(!showNew)}>
              {showNew ? (
                <EyeOff size={20} strokeWidth={1.5} />
              ) : (
                <Eye size={20} strokeWidth={1.5} />
              )}
            </div>
          </div>
        </div>

        <div className="form-group">
          <input
            id="confirm_password"
            type={showConfirm ? "text" : "password"}
            value={form.confirm_password}
            onChange={(e) =>
              setForm({ ...form, confirm_password: e.target.value })
            }
            placeholder=" "
            className="password-input"
            autoComplete="new-password"
          />
          <label htmlFor="confirm_password">تکرار کلمه عبور جدید</label>

          <div
            className="eye-icon"
            onClick={() => setShowConfirm(!showConfirm)}
          >
            {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
          </div>
        </div>

        <div className="form-group">
          <div className="strength-container">
            <div className="strength-wrapper">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`segment ${strength >= level ? "active" : ""}`}
                ></div>
              ))}
            </div>

            {form.new_password.length > 0 && (
              <div className="strength-label">
                {strengthErrors.map((err, index) => (
                  <div key={index} className="strength-error">
                    {err}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group-update">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className=""
          >
            <UserPen size={24} color="#ffffff" strokeWidth={1.5} />
            {loading ? (
              <span className="loader"></span>
            ) : (
              <span>تغییر کلمه عبور</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
